import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Eye, Loader2, LogOut, Search, Smartphone } from "lucide-react";
import { errMsg, reApi } from "@/lib/residentApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const nameOf = (r) => `${r.preferred_name || r.first_name || ""} ${r.last_name || ""}`.trim();

/** Resident Portal card: pick a resident, then use their live portal; every change is recorded as the staff member's. */
export default function RePortalAsContent() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { reApi.get("/residents").then((d) => setRows(d.residents.filter((r) => r.status === "active"))).catch((e) => setError(errMsg(e))); }, []);
  const shown = useMemo(() => (rows || []).filter((r) => nameOf(r).toLowerCase().includes(q.toLowerCase())).sort((a, b) => nameOf(a).localeCompare(nameOf(b))), [rows, q]);
  const open = async (r) => {
    setBusy(r.id); setError(null);
    try { setSession(await reApi.post(`/residents/${r.id}/portal-session`, {})); } catch (e) { setError(errMsg(e)); }
    setBusy(null);
  };
  const exit = async () => {
    const s = session; setSession(null);
    try { await reApi.del(`/portal-session?token=${encodeURIComponent(s.token)}`); } catch { /* session also expires on its own */ }
  };
  useEffect(() => {
    if (!session) return undefined;
    const end = () => fetch(`${API}/re/portal-session?token=${encodeURIComponent(session.token)}`, { method: "DELETE", keepalive: true, headers: { Authorization: `Bearer ${localStorage.getItem("nex_token") || ""}` } }).catch(() => {});
    window.addEventListener("pagehide", end);
    return () => { window.removeEventListener("pagehide", end); end(); };
  }, [session]);
  const box = useRef(null);
  const [h, setH] = useState(640);
  useLayoutEffect(() => {
    if (!session) return undefined;
    const fit = () => {
      const el = box.current;
      if (!el) return;
      let p = el.parentElement;
      while (p && !/auto|scroll/.test(getComputedStyle(p).overflowY)) p = p.parentElement;
      const bottom = p && p !== document.body && p !== document.documentElement ? p.getBoundingClientRect().bottom : window.innerHeight;
      setH(Math.max(420, Math.floor(bottom - el.getBoundingClientRect().top)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [session]);

  if (session) {
    return (
      <div ref={box} data-testid="re-portal-as-session" className="flex flex-col" style={{ height: h }}>
        <div data-testid="re-portal-as-bar" className="flex items-center gap-2 px-3 py-1.5 bg-nex-gold text-black">
          <Eye className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 min-w-0 truncate text-xs font-semibold">Viewing as {session.resident.name} · changes are recorded as you</span>
          <button type="button" onClick={exit} data-testid="re-portal-as-exit" className="inline-flex items-center gap-1 px-3 h-9 border border-black/40 rounded-sm font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-black/10">
            <LogOut className="h-3 w-3" /> Exit
          </button>
        </div>
        <iframe data-testid="re-portal-as-frame" title={`Resident portal · ${session.resident.name}`} src={session.path} className="flex-1 w-full border-0 bg-[#0B1220]" />
      </div>
    );
  }
  return (
    <div data-testid="re-portal-as" className="p-5 space-y-4">
      <div className="flex items-start gap-3">
        <Smartphone className="h-5 w-5 text-nex-gold shrink-0 mt-0.5" />
        <p className="text-xs text-nex-muted">Pick a resident to open their portal exactly as they see it. Everything works — homework, check-ins, savings requests, signing — and each change is recorded as done by you for them.</p>
      </div>
      <label className="flex items-center gap-2 border border-white/10 rounded-sm px-3 py-2">
        <Search className="h-4 w-4 text-nex-muted" />
        <input data-testid="re-portal-as-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search residents" className="flex-1 min-w-0 bg-transparent outline-none text-sm text-nex-silver" />
      </label>
      {error && <p data-testid="re-portal-as-error" className="text-sm text-red-300">{error}</p>}
      {!rows ? <div className="flex items-center gap-2 text-nex-muted text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading residents…</div> : (
        <ul className="divide-y divide-white/5 border border-white/10 rounded-sm">
          {shown.map((r) => (
            <li key={r.id}>
              <button type="button" disabled={!!busy} onClick={() => open(r)} data-testid={`re-portal-as-pick-${r.id}`} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-nex-gold/5 disabled:opacity-50">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-nex-silver truncate">{nameOf(r)}</span>
                  <span className="block text-[11px] text-nex-muted truncate">{[r.current_phase && `Phase ${r.current_phase}`, r.property_name || r.room].filter(Boolean).join(" · ")}</span>
                </span>
                {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin text-nex-gold" /> : <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-nex-gold">Open portal</span>}
              </button>
            </li>
          ))}
          {!shown.length && <li className="px-4 py-3 text-sm text-nex-muted">No active residents match.</li>}
        </ul>
      )}
    </div>
  );
}
